import React, { useEffect, useRef, useState } from "react";

import { ImageTextSlidesBlock as Value } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { notEmptyOrBlank } from "@thelabnyc/thelabui/src/utils/functional";

import { Carousel } from "../Carousel/Carousel";
import { Clickable } from "../Clickables";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

type SlideProps = NonNullable<Value["slides"]>[number]["value"];

const Slide = ({ image, url }: SlideProps) => {
    if (!image) return <></>;

    const imageBlock = (
        <ImageChooserBlock
            attrs={{ className: styles.imageContainer }}
            value={image}
            sizes="(max-width: 767px) 64vw, (max-width: 1023px) 28vw, (max-width: 1919px) 15vw, 330px"
            imageAttrs={{ className: styles.image }}
        />
    );

    const href = url?.url ?? url?.anchor;

    return (
        <div className={styles.slide}>
            {url && href ? (
                <Clickable href={href} className={styles.clickable}>
                    {imageBlock}
                    {url.display_name}
                </Clickable>
            ) : (
                <>{imageBlock}</>
            )}
        </div>
    );
};

const slidesVisibleDesktop = 5;
const slidesVisibleTablet = 3;
const slidesVisibleMobile = 1.5;

export const ImageTextSlides = ({ value: { slides } }: { value: Value }) => {
    const [progress, setProgress] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);

    const calculateProgress = (element: HTMLDivElement) => {
        setProgress(
            ((element.scrollLeft + element.clientWidth) / element.scrollWidth) *
                100,
        );
    };

    useEffect(() => {
        if (!scrollRef.current) return;
        calculateProgress(scrollRef.current);
    }, []);

    if (!slides) return <></>;

    const slideLength = slides.filter(notEmptyOrBlank).length;

    /**
     * If we don't have enough slides to trigger the scroll, we want to center
     * the items, but I couldn't figure out how to do that without changing
     * the CSS.
     */
    let noScrollBreakpoint = "";
    if (slideLength < slidesVisibleDesktop)
        noScrollBreakpoint = styles.noScrollDesktop;
    if (slideLength < slidesVisibleTablet)
        noScrollBreakpoint = styles.noScrollTablet;
    if (slideLength < slidesVisibleMobile)
        noScrollBreakpoint = styles.noScrollMobile;

    return (
        <section
            className={styles.root}
            style={{
                "--slides-visible-desktop": slidesVisibleDesktop,
                "--slides-visible-tablet": slidesVisibleTablet,
                "--slides-visible-mobile": slidesVisibleMobile,
            }}
        >
            <Carousel
                label="Media feed"
                containerProps={{ className: styles.root }}
                scrollContainerProps={{
                    className: styles.inner,
                    ref: scrollRef,
                }}
                slideWrapperProps={{ className: noScrollBreakpoint }}
                slideContainerProps={{ className: styles.listItem }}
                onScroll={({ target }) => {
                    if (!(target instanceof HTMLDivElement)) return;
                    calculateProgress(target);
                }}
                controls={({ allVisible }) => {
                    if (allVisible) return null;
                    return (
                        <div className={styles.controls}>
                            <Clickable
                                className={styles.arrowLeft}
                                slot="previous"
                            >
                                <Svg name="caret-right" />
                            </Clickable>
                            <Clickable
                                className={styles.arrowRight}
                                slot="next"
                            >
                                <Svg name="caret-right" />
                            </Clickable>
                        </div>
                    );
                }}
                dots={null}
            >
                {slides.filter(notEmptyOrBlank).map((slide) => (
                    <Slide {...slide.value} key={slide.id} />
                ))}
            </Carousel>
            <div
                className={styles.progressBar}
                style={{
                    "--scroll-progress-decimal": progress,
                    "--scroll-progress-percent": `${progress}%`,
                }}
                aria-hidden="true"
            />
        </section>
    );
};
